import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { SebWithHover } from '../../../@resources/anim/SebCommon';
import { SebMeeting } from '../../../@resources/anim/SebMeeting';
import { SebNormalBagged } from '../../../@resources/anim/SebNormalBagged';
import { SebPrivate } from '../../../@resources/anim/SebPrivate';
import { SebPrivateBagged } from '../../../@resources/anim/SebPrivateBagged';
import { SebPublic } from '../../../@resources/anim/SebPublic';
import { SebPublicBagged } from '../../../@resources/anim/SebPublicBagged';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Grid } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { useResolve } from '../../Container';
import { Copy, Divider, Flexer } from '../../dashboard/elements/LayoutElements';
import { concealAll, revealAll } from '../../packages/conceal/ConcealCommands';
import { ConcealmentInfo } from '../../packages/conceal/ConcealmentInfo';
import { FeatureManager } from '../../packages/feature/FeatureManager';
import { IncognitoInfo } from '../../packages/incognito/IncognitoInfo';
import { useEditorSessionObserver } from '../EditorSession';
import { Popper, PopperButtonProps, PopperContentProps } from '../ui/Popper';
import { EditorModeWidgetButton, EditorModeWidgetContent, EditorModeWidgetFrame } from './EditorModeWidgetElements';

const chooseSeb = (conceal: ConcealmentInfo, incognito: IncognitoInfo) => {
    if (conceal.enabled) {
        if (incognito.enabled) {
            return conceal.mode === 'public' ? SebPublicBagged : SebPrivateBagged;
        }

        else {
            return conceal.mode === 'public' ? SebPublic : SebPrivate;
        }
    }
    else if (incognito.enabled) {
        return SebNormalBagged;
    }
    else {
        return SebMeeting;
    }
};

const SelectButton = styled(DropButton)`
    min-width: 15rem;
    ${Copy} {
        font-weight: 450 !important;
        margin: 0 .5rem 0 0 !important;
    }
`;

const PopperButton = ({ onClick }: PopperButtonProps) => {
    const conceal = useEditorSessionObserver(ConcealmentInfo);
    const incognito = useEditorSessionObserver(IncognitoInfo);

    const color = useMemo(() =>
        (conceal.enabled)
            ? (conceal.mode === 'public' ? Colors.positive : Colors.negative)
            : Colors.primary, [conceal]
    );

    const message = useMemo(() => {
        if (conceal.enabled) {
            const mostly = conceal.mostly ? <u>mostly</u> : null;
            const tail = incognito.enabled ? ' & you are anonymous' : '';
            return <span>Your stickies are {mostly} {conceal.mode}{tail}</span>;
        }
        if (incognito.enabled) {
            return <span>You are anonymous</span>;
        }
        return <span>You are in Meeting Mode</span>;
    }, [conceal, incognito]);

    return <EditorModeWidgetButton color={color} onClick={onClick}>
        <span>{message}</span>
        <Icon size=".875rem" src="deqo://icons/hero/chevron-up" />
    </EditorModeWidgetButton>;
};

const PopperContent = ({ onClose }: PopperContentProps) => {
    const dispatch = useResolve(CommandDispatcher);
    const conceal = useEditorSessionObserver(ConcealmentInfo);
    const incognito = useEditorSessionObserver(IncognitoInfo);

    const onConcealReveal = useCallback((mode: string) => {
        dispatch(mode === 'public' ? revealAll : concealAll);
        onClose();
    }, [onClose]);

    return <EditorModeWidgetContent>
        <Flexer padding="1">
            {incognito.enabled &&
                <Flexer flex="row" align="center" padding="1" gap="1.25" minWidth="32" background="ghostwhite">
                    <Flexer shrink>
                        <Icon size="3" src="deqo://icons/meeting/anonymous" />
                    </Flexer>
                    <Copy bold flush size=".875" color={Colors.textLight}>
                        Your host has set everyone to anonymous.
                        Stickies &amp; other objects you create cannot be tied back to your identity.
                    </Copy>
                </Flexer>
            }
            {incognito.enabled && conceal.enabled &&
                <Divider mode="horizontal" margin="1 0" />
            }
            {conceal.enabled && <>
                <Grid columns="1fr .125rem 1fr" columnGap="1rem">
                    <SelectButton padding=".5" onClick={() => onConcealReveal('private')}>
                        <SebPrivate />
                        <Copy color={Colors.textLight}>Hide your stickies <br /> from everyone</Copy>
                    </SelectButton>
                    <Divider mode="vertical" />
                    <SelectButton padding=".5" onClick={() => onConcealReveal('public')}>
                        <SebPublic />
                        <Copy color={Colors.textLight}>Make your stickies <br /> visible to everyone</Copy>
                    </SelectButton>
                </Grid>
            </>}
            {!incognito.enabled && !conceal.enabled &&
                <Flexer width="32">
                    <Copy flush color={Colors.textLight}>
                        No special collaboration features are enabled at the moment.
                        If your meeting host enables any (e.g. Private Writing), this
                        widget will change to provide UI specific for the features enabled.
                    </Copy>
                </Flexer>}
        </Flexer>
        <BetterPopArrow placement="top" color="#fff" border={[2, Colors.border]} size="1.5" style={{ left: '50%', marginTop: '.125rem' }} />
    </EditorModeWidgetContent>;
};

export const MeetingModeWidget = () => {

    const conceal = useEditorSessionObserver(ConcealmentInfo);
    const incognito = useEditorSessionObserver(IncognitoInfo);
    const features = useObserver(useResolve(FeatureManager));

    if (features.get('Hands')) return null;

    return <EditorModeWidgetFrame>
        <SebWithHover seb={chooseSeb(conceal, incognito)} />
        <Popper
            placement="top"
            button={PopperButton}
            content={PopperContent}
            inner={{ direction: 'up', offset: '1' }}
        />
    </EditorModeWidgetFrame>;
};
